<script setup lang="ts" generic="T extends Record<string, unknown>">
import { computed } from "vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import type { Column, ColumnAlign } from "./types";

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** Column definitions. Optional when using #thead/#tbody override slots. */
    columns?: Column[];
    /** Row data. Optional when using #tbody override slot. */
    rows?: T[];
    /** Returns a unique key per row. Optional when using #tbody override slot. */
    rowKey?: (row: T) => string | number;
    /** Show a loading spinner in place of the table body. */
    loading?: boolean;
    /** Text shown when rows is empty and loading is false. */
    emptyState?: string;
    /** Screen-reader-only table caption for accessibility. */
    caption?: string;
    /** Pin the header row to the top of its scroll container. */
    stickyHeader?: boolean;
    /** Extra Tailwind classes applied to the <table> element. */
    tableClass?: string;
  }>(),
  {
    columns: () => [],
    rows: () => [] as T[],
    rowKey: () => () => "",
    loading: false,
    emptyState: "No data available",
    caption: undefined,
    stickyHeader: false,
    tableClass: undefined,
  }
);

const emit = defineEmits<{
  sort: [column: Column];
}>();

const alignClass = (align?: ColumnAlign) => {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "text-left";
};

const cellValue = (row: T, key: string): unknown => row[key];

const isEmpty = computed(() => !props.loading && props.rows.length === 0);
</script>

<template>
  <!-- tabindex=0 on the scroll wrapper so keyboard users can scroll horizontally; role=region + aria-label name the scrollable area. -->
  <div
    v-bind="$attrs"
    tabindex="0"
    role="region"
    :aria-label="caption ?? 'Table'"
    class="focus-ring w-full overflow-x-auto">
    <table :class="['w-full border-collapse', tableClass]">
      <caption
        v-if="caption"
        class="sr-only">
        {{
          caption
        }}
      </caption>

      <!-- Override slot for fully-custom thead (e.g. complex headers, vertical text) -->
      <slot name="thead">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              scope="col"
              :style="col.width ? { width: col.width } : undefined"
              :aria-sort="col.sortable ? col.sortDirection ?? 'none' : undefined"
              :class="[
                't-small-bold text-type-muted border-b border-slate-200 bg-slate-100 px-4 py-3',
                alignClass(col.align),
                stickyHeader && 'z-sticky sticky top-0',
              ]">
              <!-- Override slot for a single header cell: #header-<key> -->
              <slot :name="`header-${col.key}`">
                <!-- Sortable header renders as a real button: Tab-reachable, Enter/Space activatable, focus-ring visible. -->
                <button
                  v-if="col.sortable"
                  type="button"
                  class="focus-ring rounded-4 duration-fast ease-standard -mx-2 inline-flex cursor-pointer items-center gap-1 px-2 py-1 transition-colors select-none hover:bg-slate-200"
                  @click="emit('sort', col)">
                  {{ col.label }}
                </button>
                <template v-else>{{ col.label }}</template>
              </slot>
            </th>
          </tr>
        </thead>
      </slot>

      <!-- Override slot for fully-custom tbody (e.g. matrix tables, grouped rows) -->
      <slot name="tbody">
        <tbody>
          <!-- Loading state -->
          <tr v-if="loading">
            <td
              :colspan="columns.length || 1"
              class="px-4 py-8 text-center">
              <div class="flex justify-center">
                <LoadingSpinner
                  size="md"
                  fill="gray" />
              </div>
            </td>
          </tr>

          <!-- Empty state -->
          <tr v-else-if="isEmpty">
            <td
              :colspan="columns.length || 1"
              class="text-type-muted t-small px-4 py-8 text-center">
              <slot name="empty">
                {{ emptyState }}
              </slot>
            </td>
          </tr>

          <!-- Data rows -->
          <template v-else>
            <tr
              v-for="row in rows"
              :key="rowKey(row)"
              class="duration-fast ease-standard border-b border-slate-200 transition-colors hover:bg-slate-50">
              <td
                v-for="col in columns"
                :key="col.key"
                :class="['text-type-default t-small px-4 py-3', alignClass(col.align)]">
                <!--
                  Override slot for a single data cell: #cell-<key>="{ row, value }"
                  - row: the full row object (type T)
                  - value: the raw cell value (row[col.key])
                -->
                <slot
                  :name="`cell-${col.key}`"
                  :row="row"
                  :value="cellValue(row, col.key)">
                  {{ cellValue(row, col.key) }}
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </slot>

      <!-- Optional footer slot -->
      <tfoot v-if="$slots.footer">
        <slot name="footer" />
      </tfoot>
    </table>
  </div>
</template>
